import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatPhoneNumber } from '../utils/formatters';
import {
  Receipt,
  Plus,
  Search,
  AlertCircle,
  Clock,
  Mail,
  CheckCircle,
  Edit,
  Download,
  Trash2,
  RotateCcw,
  Info,
  Calendar,
  DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import GroupedInvoiceGrid from '../components/grid/GroupedInvoiceGrid';
import InvoiceStatsCards from '../components/invoice/InvoiceStatsCards';
import CreateInvoiceModal from '../components/invoice/CreateInvoiceModal';
import Modal from '../components/ui/Modal';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { InvoiceDto, InvoiceStatus, InvoiceStatistics, InvoicePaymentSummary } from '../types';
import DateRangeFilter, { DateRange } from '../components/invoice/DateRangeFilter';
import { formatTimestamp } from '../utils/formatters';
import { useCompanyTimeZone } from '../hooks/useCompanyTimeZone';
import { generateInvoiceHTML, InvoiceV2State } from '../utils/invoiceV2PdfGenerator';
import { generateInvoicePDFFromData } from '../utils/invoiceV2PdfGenerator';

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { companyTimeZone } = useCompanyTimeZone();
  const [activeTab, setActiveTab] = useState<'active' | 'archived' | 'deleted'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDto | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceDto | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [invoiceToArchive, setInvoiceToArchive] = useState<InvoiceDto | null>(null);

  // Date range for statistics (default: This Year)
  const now = new Date();
  const defaultFrom = new Date(now.getFullYear(), 0, 1);
  const defaultTo = new Date(now.getFullYear(), 11, 31);
  const [dateRange, setDateRange] = useState<DateRange>({ from: defaultFrom, to: defaultTo, label: 'This Year' });

  const formatMDY = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const queryClient = useQueryClient();

  // Fetch invoice statistics
  const { data: statisticsData, isLoading: statsLoading } = useQuery({
    queryKey: ['invoice-statistics', dateRange.from?.toISOString(), dateRange.to?.toISOString()],
    queryFn: () => apiService.getInvoiceStatistics(
      dateRange.from ? dateRange.from.toISOString().slice(0,10) : undefined,
      dateRange.to ? dateRange.to.toISOString().slice(0,10) : undefined
    ),
    enabled: activeTab === 'active'
  });

  // Fetch invoices
  const { data: invoicesData, isLoading, error } = useQuery({
    queryKey: ['invoices', searchTerm, statusFilter],
    queryFn: () => apiService.getInvoices(1, 100, searchTerm),
    enabled: activeTab === 'active',
    retry: 1,
    retryDelay: 1000
  });

  console.log('Invoices query state:', {
    invoicesData,
    isLoading,
    error,
    activeTab,
    enabled: activeTab === 'active'
  });

  // Extract unique work order IDs from invoices
  const workOrderIds = useMemo(() => {
    console.log('invoicesData:', invoicesData);
    console.log('invoicesData?.items:', invoicesData?.items);
    console.log('invoicesData keys:', invoicesData ? Object.keys(invoicesData) : 'no data');
    
    if (!invoicesData?.items) {
      console.log('No items array found in invoicesData');
      return [];
    }
    
    const ids = new Set<number>();
    invoicesData.items.forEach((invoice, index) => {
      console.log(`Invoice ${index}:`, {
        id: invoice.id,
        workOrderId: invoice.workOrderId,
        allKeys: Object.keys(invoice)
      });
      if (invoice.workOrderId) {
        ids.add(invoice.workOrderId);
      }
    });
    console.log('Extracted work order IDs:', Array.from(ids));
    return Array.from(ids);
  }, [invoicesData]);

  // Force enable the query if we have work order IDs
  const shouldFetchPaymentSummaries = workOrderIds.length > 0 && activeTab === 'active';

  // Test API call for debugging
  useEffect(() => {
    if (workOrderIds.length > 0) {
      console.log('Testing API call for work order ID:', workOrderIds[0]);
      apiService.getWorkOrderInvoicesSummary(workOrderIds[0])
        .then(result => console.log('API test result:', result))
        .catch(error => console.error('API test error:', error));
    }
  }, [workOrderIds]);

  // Fetch payment summaries for all work orders using batch endpoint
  const { data: paymentSummariesData, isLoading: paymentSummariesLoading } = useQuery({
    queryKey: ['payment-summaries', workOrderIds],
    queryFn: async () => {
      console.log('Fetching payment summaries for work order IDs:', workOrderIds);
      if (workOrderIds.length === 0) {
        return new Map<number, InvoicePaymentSummary>();
      }

      try {
        // Use batch endpoint to fetch all summaries in a single request
        const batchResponse = await apiService.getWorkOrderInvoicesSummariesBatch(workOrderIds);
        console.log('Batch payment summaries response:', batchResponse);
        
        if (batchResponse.success && batchResponse.data) {
          // Convert the record to a Map
          const summaries = new Map<number, InvoicePaymentSummary>();
          Object.entries(batchResponse.data).forEach(([workOrderIdStr, summary]) => {
            const workOrderId = parseInt(workOrderIdStr);
            summaries.set(workOrderId, summary);
          });
          console.log('All payment summaries:', summaries);
          return summaries;
        }
        return new Map<number, InvoicePaymentSummary>();
      } catch (error) {
        console.error('Failed to fetch payment summaries batch:', error);
        return new Map<number, InvoicePaymentSummary>();
      }
    },
    enabled: shouldFetchPaymentSummaries,
    staleTime: 0, // Always refetch when invalidated
    retry: 1,
    retryDelay: 1000
  });

  console.log('Payment summaries query conditions:', {
    workOrderIdsLength: workOrderIds.length,
    activeTab,
    enabled: shouldFetchPaymentSummaries,
    paymentSummariesData,
    paymentSummariesLoading
  });

  // Fetch deleted invoices
  const { data: deletedInvoicesData, isLoading: deletedLoading, error: deletedError } = useQuery({
    queryKey: ['deleted-invoices', searchTerm],
    queryFn: () => apiService.getDeletedInvoices(1, 100, searchTerm),
    enabled: activeTab === 'deleted'
  });

  // Fetch archived invoices
  const { data: archivedInvoicesData, isLoading: archivedLoading, error: archivedError } = useQuery({
    queryKey: ['archived-invoices', searchTerm],
    queryFn: () => apiService.getArchivedInvoices(1, 100, searchTerm),
    enabled: activeTab === 'archived'
  });

  // Delete invoice mutation
  const deleteInvoiceMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteInvoice(id),
    onSuccess: () => {
      toast.success('Invoice deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
    },
    onError: (error: any) => {
      toast.error('Failed to delete invoice');
      console.error('Error deleting invoice:', error);
    }
  });

  // Mark as sent mutation
  const markSentMutation = useMutation({
    mutationFn: (id: number) => apiService.markInvoiceAsSent(id),
    onSuccess: () => {
      toast.success('Invoice marked as sent');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
    },
    onError: (error: any) => {
      toast.error('Failed to mark invoice as sent');
      console.error('Error marking invoice as sent:', error);
    }
  });

  // Mark as paid mutation
  const markPaidMutation = useMutation({
    mutationFn: (id: number) => apiService.markInvoiceAsPaid(id),
    onSuccess: async () => {
      toast.success('Invoice marked as paid');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
      console.log('Invalidated payment summaries after marking as paid');
      
      // Force refetch payment summaries immediately
      await queryClient.refetchQueries({ queryKey: ['payment-summaries'] });
      console.log('Refetched payment summaries');
    },
    onError: (error: any) => {
      toast.error('Failed to mark invoice as paid');
      console.error('Error marking invoice as paid:', error);
    }
  });

  // Restore invoice mutation
  const restoreInvoiceMutation = useMutation({
    mutationFn: (id: number) => apiService.restoreInvoice(id),
    onSuccess: () => {
      toast.success('Invoice restored successfully');
      queryClient.invalidateQueries({ queryKey: ['deleted-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
    },
    onError: (error: any) => {
      toast.error('Failed to restore invoice');
      console.error('Error restoring invoice:', error);
    }
  });

  // Archive/Unarchive mutations
  const archiveInvoiceMutation = useMutation({
    mutationFn: (id: number) => apiService.archiveInvoice(id),
    onSuccess: () => {
      toast.success('Invoice archived');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['archived-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
      setIsArchiveModalOpen(false);
      setInvoiceToArchive(null);
    },
    onError: (error: any) => {
      toast.error('Failed to archive invoice');
      console.error('Error archiving invoice:', error);
    }
  });

  const unarchiveInvoiceMutation = useMutation({
    mutationFn: (id: number) => apiService.unarchiveInvoice(id),
    onSuccess: () => {
      toast.success('Invoice unarchived');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['archived-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
    },
    onError: (error: any) => {
      toast.error('Failed to unarchive invoice');
      console.error('Error unarchiving invoice:', error);
    }
  });

  // Bulk operations mutations
  const bulkMarkAsSentMutation = useMutation({
    mutationFn: ({ invoiceIds, sendEmail }: { invoiceIds: number[]; sendEmail: boolean }) => 
      apiService.bulkMarkAsSent(invoiceIds, sendEmail),
    onSuccess: (result) => {
      if (result.successCount > 0) {
        toast.success(`Successfully marked ${result.successCount} invoice${result.successCount === 1 ? '' : 's'} as sent`);
      }
      if (result.failedCount > 0) {
        toast.error(`Failed to mark ${result.failedCount} invoice${result.failedCount === 1 ? '' : 's'} as sent`);
        if (result.errors.length > 0) {
          console.error('Bulk mark as sent errors:', result.errors);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
    },
    onError: (error: any) => {
      toast.error('Failed to mark invoices as sent');
      console.error('Error bulk marking invoices as sent:', error);
    }
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: (invoiceIds: number[]) => apiService.bulkArchive(invoiceIds),
    onSuccess: (result) => {
      if (result.successCount > 0) {
        toast.success(`Successfully archived ${result.successCount} invoice${result.successCount === 1 ? '' : 's'}`);
      }
      if (result.failedCount > 0) {
        toast.error(`Failed to archive ${result.failedCount} invoice${result.failedCount === 1 ? '' : 's'}`);
        if (result.errors.length > 0) {
          console.error('Bulk archive errors:', result.errors);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['archived-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
    },
    onError: (error: any) => {
      toast.error('Failed to archive invoices');
      console.error('Error bulk archiving invoices:', error);
    }
  });

  // Create invoice helpers
  const createInvoice = async (args: { workOrderId: number; type: 'deposit'|'progress'|'change'|'final'; depositPercentage?: number; description?: string; }) => {
    switch (args.type) {
      case 'deposit':
        if (!args.depositPercentage) throw new Error('Deposit percentage required');
        return apiService.createDepositInvoice(args.workOrderId, args.depositPercentage);
      case 'progress':
        return apiService.createProgressInvoice(args.workOrderId, args.description);
      case 'change':
        return apiService.createChangeOrderInvoice(args.workOrderId, args.description);
      case 'final':
        return apiService.createFinalInvoice(args.workOrderId);
    }
  };

  const createInvoiceMutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: (inv: any) => {
      toast.success(`Invoice ${inv.invoiceNumber || ''} created`);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summaries'] });
      setShowCreateModal(false);
      navigate(`/invoices/${inv.id}/edit`);
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || e?.message || 'Failed to create invoice';
      toast.error(msg);
      console.error('Create invoice error:', e);
    }
  });

  const handleViewDetails = async (invoice: InvoiceDto) => {
    try {
      // Fetch full invoice data with all details for PDF preview
      const fullInvoice = await apiService.getInvoiceById(invoice.id);
      setSelectedInvoice(fullInvoice);
      setShowDetails(true);
    } catch (error) {
      console.error('Error loading invoice details:', error);
      toast.error('Failed to load invoice details');
    }
  };

  // Convert InvoiceDto to InvoiceV2State for PDF preview
  const mapInvoiceToV2State = (invoice: InvoiceDto): InvoiceV2State => {
    return {
      // Header
      logo: invoice.companyLogoBase64 || '',
      companyName: invoice.companyName || '',
      companyAddress: [
        invoice.companyAddress,
        invoice.companyCity,
        invoice.companyState,
        invoice.companyZipCode
      ].filter(Boolean).join(', '),
      companyPhone: invoice.companyPhone || '',
      companyEmail: invoice.companyEmail || '',
      companyWebsite: '',

      // Recipient
      recipientName: invoice.clientName || '',
      recipientAddress: [
        invoice.clientAddress,
        invoice.clientCity,
        invoice.clientState,
        invoice.clientZipCode
      ].filter(Boolean).join(', '),
      recipientPhone: invoice.clientPhone || '',
      recipientEmail: invoice.clientEmail || '',

      // Details
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: new Date(invoice.invoiceDate),
      dueDate: new Date(invoice.dueDate),

      // Line Items
      items: invoice.items.map(item => ({
        id: item.id.toString(),
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
        jobContactEntryId: item.jobContactEntryId
      })),

      // Financials
      taxRate: invoice.taxRate,
      discountAmount: invoice.discountAmount || 0,
      discountType: (invoice.discountType as 'None' | 'Percentage' | 'Fixed') || 'None',
      discountPercentage: invoice.discountPercentage || 0,
      discountDescription: invoice.discountDescription || '',
      amountPaid: invoice.amountPaid || 0,

      // Additional
      paymentInfo: invoice.paymentInfo || '',
      terms: invoice.terms || '',
      notes: invoice.notes || ''
    };
  };

  const handleCreateCreditMemo = (invoice: InvoiceDto) => {
    navigate(`/credit-memos/create?invoiceId=${invoice.id}`);
  };

  // Handle print from preview modal
  const handlePrintInvoice = async (invoice: InvoiceDto) => {
    try {
      const invoiceData = mapInvoiceToV2State(invoice);
      const invoiceHtml = generateInvoiceHTML(invoiceData);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Invoice ${invoice.invoiceNumber}</title>
              <style>
                body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                @media print { body { margin: 0; padding: 0; } }
              </style>
            </head>
            <body>
              ${invoiceHtml}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    } catch (error) {
      console.error('Error printing invoice:', error);
      toast.error('Failed to print invoice');
    }
  };

  const handleDelete = (invoice: InvoiceDto) => {
    setInvoiceToDelete(invoice);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (invoiceToDelete) {
      deleteInvoiceMutation.mutate(invoiceToDelete.id);
    }
  };

  const handleDownloadPdf = async (invoiceId: number, invoiceNumber: string) => {
    // legacy method intentionally left unused now
  };

  // Helper function to build complete address
  const buildCompleteAddress = (address: string, city: string, state: string, zipCode: string) => {
    const parts = [address, city, state, zipCode].filter(part => part && part.trim());
    return parts.join('\n');
  };

  const handleDownloadInvoicePdf = async (inv: InvoiceDto) => {
    try {
      console.log('Invoice data from grid:', inv);
      
      // Fetch the full invoice data (same as invoice editor does)
      const fullInvoice = await apiService.getInvoiceById(inv.id);
      console.log('Full invoice data for PDF:', fullInvoice);
      
      // Use our mapping function to convert to InvoiceV2State format
      const invoiceData = mapInvoiceToV2State(fullInvoice);
      
      // Generate and download the PDF
      await generateInvoicePDFFromData(invoiceData, fullInvoice.invoiceNumber);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Bulk operation handlers
  const handleBulkMarkAsSent = (invoiceIds: number[], sendEmail: boolean) => {
    bulkMarkAsSentMutation.mutate({ invoiceIds, sendEmail });
  };

  const handleBulkArchive = (invoiceIds: number[]) => {
    bulkArchiveMutation.mutate(invoiceIds);
  };

  const handleBulkExport = async (invoiceIds: number[]) => {
    try {
      const filter = {
        invoiceIds,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: dateRange.from?.toISOString().slice(0, 10),
        endDate: dateRange.to?.toISOString().slice(0, 10)
      };

      const blob = await apiService.exportInvoices(filter);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV export downloaded successfully');
    } catch (error) {
      console.error('Error exporting invoices:', error);
      toast.error('Failed to export invoices');
    }
  };

  const getStatusBadge = (status: InvoiceStatus) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: InvoiceStatus) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return <Clock className="h-3 w-3" />;
      case 'sent':
        return <Mail className="h-3 w-3" />;
      case 'paid':
        return <CheckCircle className="h-3 w-3" />;
      case 'partial':
        return <DollarSign className="h-3 w-3" />;
      case 'overdue':
        return <AlertCircle className="h-3 w-3" />;
      case 'cancelled':
        return <AlertCircle className="h-3 w-3" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };


  const filteredInvoices = useMemo(() => {
    if (activeTab === 'active') {
      if (!invoicesData?.items) return [];
      
      let filtered = invoicesData.items;
      
      if (statusFilter !== 'all') {
        filtered = filtered.filter(invoice => 
          invoice.status.toLowerCase() === statusFilter.toLowerCase()
        );
      }
      
      return filtered;
    } else {
      // For deleted invoices, we don't filter by status since they're all "deleted"
      return deletedInvoicesData?.invoices || [];
    }
  }, [invoicesData?.items, statusFilter, activeTab, deletedInvoicesData?.invoices]);

  if (error || deletedError) {
    return (
      <div className="p-6">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Error Loading Invoices
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            There was an error loading the invoices. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Receipt className="h-8 w-8 text-gray-900 dark:text-gray-100" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Invoices
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage and track your invoices
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Create Invoice</span>
          </Button>
        </div>
      </div>
      

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'active'
                ? 'border-accent-color text-accent-color'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Receipt className="h-4 w-4 inline mr-2" />
            Active Invoices
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'archived'
                ? 'border-accent-color text-accent-color'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <RotateCcw className="h-4 w-4 inline mr-2 rotate-180" />
            Archived
          </button>
          <button
            onClick={() => setActiveTab('deleted')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'deleted'
                ? 'border-accent-color text-accent-color'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Trash2 className="h-4 w-4 inline mr-2" />
            Deleted Invoices
          </button>
        </nav>
      </div>

      {/* Statistics Cards - Only show for active invoices */}
      {activeTab === 'active' && statisticsData && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
              {statisticsData.dateRangeStart && statisticsData.dateRangeEnd
                ? `${formatMDY(statisticsData.dateRangeStart)} - ${formatMDY(statisticsData.dateRangeEnd)}`
                : (dateRange.label)}
            </span>
          </div>
          {statsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <InvoiceStatsCards
              stats={statisticsData}
              isLoading={statsLoading}
            />
          )}
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Info className="h-4 w-4" />
            <span>
              Statistics show all active invoices. Paid invoices are filtered by the selected date range. Archived invoices are excluded.
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
              />
            </div>
          </div>

          {/* Status Filter - Only show for active invoices */}
          {activeTab === 'active' && (
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}

          {/* Date Range Filter for statistics */}
          {activeTab === 'active' && (
            <div className="sm:w-64">
              <DateRangeFilter
                value={dateRange}
                onChange={(range) => setDateRange(range)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Grouped Invoice Grid */}
      <GroupedInvoiceGrid
        invoices={activeTab === 'active' ? filteredInvoices : activeTab === 'deleted' ? (deletedInvoicesData?.items || []) : (archivedInvoicesData?.items || [])}
        isLoading={activeTab === 'active' ? isLoading : activeTab === 'deleted' ? deletedLoading : archivedLoading}
        paymentSummaries={paymentSummariesData}
        onViewDetails={handleViewDetails}
        onEditInvoice={(invoice) => navigate(`/invoices/${invoice.id}/edit`)}
        onDeleteInvoice={activeTab === 'active' ? handleDelete : undefined}
        onMarkAsSent={activeTab === 'active' ? (invoice) => markSentMutation.mutate(invoice.id) : undefined}
        onMarkAsPaid={activeTab === 'active' ? (invoice) => markPaidMutation.mutate(invoice.id) : undefined}
        onCreateCreditMemo={activeTab === 'active' ? handleCreateCreditMemo : undefined}
        onManagePayments={activeTab === 'active' ? (invoice) => navigate(`/payments?invoiceId=${invoice.id}`) : undefined}
        onDownloadPDF={activeTab === 'active' ? handleDownloadInvoicePdf : undefined}
        onViewWorkOrder={(workOrder) => navigate(`/workorders/${workOrder.id}/edit`)}
        onRestoreInvoice={activeTab === 'deleted' ? (invoice) => restoreInvoiceMutation.mutate(invoice.id) : undefined}
        onArchiveInvoice={activeTab === 'active' ? (invoice) => { setInvoiceToArchive(invoice); setIsArchiveModalOpen(true); } : undefined}
        onUnarchiveInvoice={activeTab === 'archived' ? (invoice) => unarchiveInvoiceMutation.mutate(invoice.id) : undefined}
        showDeletedInfo={activeTab === 'deleted'}
        onBulkMarkAsSent={activeTab === 'active' ? handleBulkMarkAsSent : undefined}
        onBulkArchive={activeTab === 'active' ? handleBulkArchive : undefined}
        onBulkExport={activeTab === 'active' ? handleBulkExport : undefined}
      />

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={(args) => createInvoiceMutation.mutate(args as any)}
        isCreating={createInvoiceMutation.isPending}
      />

      {/* Invoice PDF Preview Modal */}
      <Modal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        title={`Invoice Preview - ${selectedInvoice?.invoiceNumber || ''}`}
        maxWidth="6xl"
      >
        {selectedInvoice && (
          <div className="space-y-4">
            {/* Invoice PDF Preview */}
            <div
              className="bg-white border border-gray-300 rounded-lg overflow-auto shadow-sm"
              style={{ maxHeight: '70vh' }}
              dangerouslySetInnerHTML={{ 
                __html: generateInvoiceHTML(mapInvoiceToV2State(selectedInvoice)) 
              }}
            />

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => handleDownloadInvoicePdf(selectedInvoice)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => handlePrintInvoice(selectedInvoice)}
              >
                <Receipt className="h-4 w-4 mr-2" />
                Print
              </Button>
              {!['Paid', 'Cancelled'].includes(selectedInvoice.status) && (
                <Button
                  onClick={() => navigate(`/invoices/${selectedInvoice.id}/edit`)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Invoice
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setShowDetails(false)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Invoice"
        maxWidth="md"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete invoice <strong>{invoiceToDelete?.invoiceNumber}</strong>? 
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteInvoiceMutation.mutate(invoiceToDelete!.id)}
              disabled={deleteInvoiceMutation.isPending}
            >
              {deleteInvoiceMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        isOpen={isArchiveModalOpen}
        onClose={() => { if (!archiveInvoiceMutation.isPending) { setIsArchiveModalOpen(false); setInvoiceToArchive(null); } }}
        title="Archive Invoice"
        maxWidth="md"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to archive invoice <strong>{invoiceToArchive?.invoiceNumber}</strong>? 
            Archived invoices will be moved to the archived tab.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => { if (!archiveInvoiceMutation.isPending) { setIsArchiveModalOpen(false); setInvoiceToArchive(null); } }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => archiveInvoiceMutation.mutate(invoiceToArchive!.id)}
              disabled={archiveInvoiceMutation.isPending}
            >
              {archiveInvoiceMutation.isPending ? 'Archiving...' : 'Archive'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InvoicesPage;
